import {MAX_MULTIMER_COMPONENTS} from 'model-hub/scientific-models/ScientificModelConstants';
import * as ImportUtil from 'util/ImportUtil';
import {getUserMessage} from 'util/Net';

import {
  fillMissingInputNames,
  type PredictionInput,
  makeComponent,
  makeInput,
  validateAllInputs,
} from '../newPredictionState';
import {extractComplexes, type ExtractedComplex} from './FastaExtractionDAO';
import {FileImportError, type FileImporter, type FileImportResult} from './FileImport';

function getUploadErrorMessage(error: unknown): string {
  return (
    getUserMessage(error) ??
    (error as {userMessage?: string} | null)?.userMessage ??
    'The FASTA file could not be uploaded. Please try again.'
  );
}

function uploadFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    ImportUtil.upload(file)
      .done((location: string) => {
        if (typeof location !== 'string' || location.length === 0) {
          reject(
            new FileImportError(
              'Failed to upload FASTA',
              'The upload completed without a file location. Please try again.'
            )
          );
          return;
        }
        resolve(location);
      })
      .fail((error: unknown) => {
        reject(new FileImportError('Failed to upload FASTA', getUploadErrorMessage(error)));
      });
  });
}

export function buildInputsFromExtractedComplexes(
  complexes: Array<ExtractedComplex>,
  maxInputs: number,
  usedInputNames: ReadonlySet<string> = new Set()
): FileImportResult {
  if (complexes.length === 0) {
    throw new FileImportError('No complexes found', 'The FASTA file contained no sequences.');
  }

  if (complexes.length > maxInputs) {
    throw new FileImportError(
      'Too many inputs',
      `This file has ${complexes.length} inputs, but a batch can have at most ${maxInputs}.`
    );
  }

  const inputs: Array<PredictionInput> = complexes.map((complex) => {
    if (complex.components.length === 0) {
      throw new FileImportError('No components found', `"${complex.name}" has no components to import.`);
    }
    if (complex.components.length > MAX_MULTIMER_COMPONENTS) {
      throw new FileImportError(
        'Too many components',
        `"${complex.name}" has ${complex.components.length} components, but a prediction can have at most ${MAX_MULTIMER_COMPONENTS}.`
      );
    }
    return {
      ...makeInput(),
      name: complex.name,
      components: complex.components.map((component) => ({
        ...makeComponent(component.type),
        value: component.value,
      })),
    };
  });

  const validatedInputs = validateAllInputs(fillMissingInputNames(inputs, usedInputNames));

  return {
    inputs: validatedInputs,
    summary: {
      importedComponentCount: validatedInputs.reduce((count, input) => count + input.components.length, 0),
      importedInputCount: validatedInputs.length,
      invalidInputCount: validatedInputs.filter((input) =>
        input.components.some((component) => component.status.kind === 'invalid')
      ).length,
    },
  };
}

export const fastaFileImporter: FileImporter = {
  acceptedFileTypes: {'application/octet-stream': ['.fasta', '.faa']},
  importFile: async (file, maxInputs, usedInputNames) => {
    const location = await uploadFile(file);
    return buildInputsFromExtractedComplexes(await extractComplexes([location]), maxInputs, usedInputNames);
  },
};
